import { z } from "zod";
import type { i18n as I18nInstance } from "i18next";

let i18nRef: I18nInstance | null = null;

const zodErrorMap: z.ZodErrorMap = (issue, ctx) => {
  const t = (key: string, options?: Record<string, unknown>) =>
    i18nRef
      ? i18nRef.t(key, { defaultValue: ctx.defaultError, ...options })
      : ctx.defaultError;

  switch (issue.code) {
    case z.ZodIssueCode.invalid_type: {
      if (issue.received === "undefined" || issue.received === "null") {
        return { message: t("zod.required") };
      }
      return {
        message: t("zod.invalidType", {
          expected: issue.expected,
          received: issue.received,
        }),
      };
    }

    case z.ZodIssueCode.invalid_string: {
      if (issue.validation === "email") {
        return { message: t("zod.invalidEmail") };
      }
      if (issue.validation === "url") {
        return { message: t("zod.invalidUrl") };
      }
      if (issue.validation === "uuid") {
        return { message: t("zod.invalidUuid") };
      }
      if (issue.validation === "regex") {
        return { message: t("zod.invalidFormat") };
      }
      return { message: t("zod.invalidString") };
    }

    case z.ZodIssueCode.too_small: {
      const min = issue.minimum as number;
      if (issue.type === "string") {
        if (min === 1) return { message: t("zod.required") };
        return { message: t("zod.stringMin", { min, count: min }) };
      }
      if (issue.type === "number") {
        return { message: t("zod.numberMin", { min }) };
      }
      if (issue.type === "array") {
        return { message: t("zod.arrayMin", { min, count: min }) };
      }
      if (issue.type === "date") {
        return { message: t("zod.dateMin", { min }) };
      }
      return { message: t("zod.tooSmall", { min }) };
    }

    case z.ZodIssueCode.too_big: {
      const max = issue.maximum as number;
      if (issue.type === "string") {
        return { message: t("zod.stringMax", { max, count: max }) };
      }
      if (issue.type === "number") {
        return { message: t("zod.numberMax", { max }) };
      }
      if (issue.type === "array") {
        return { message: t("zod.arrayMax", { max, count: max }) };
      }
      if (issue.type === "date") {
        return { message: t("zod.dateMax", { max }) };
      }
      return { message: t("zod.tooBig", { max }) };
    }

    case z.ZodIssueCode.invalid_enum_value:
      return {
        message: t("zod.invalidEnum", {
          options: issue.options.join(", "),
        }),
      };

    case z.ZodIssueCode.invalid_date:
      return { message: t("zod.invalidDate") };

    case z.ZodIssueCode.custom:
      return { message: issue.message ?? t("zod.invalid") };

    default:
      return { message: ctx.defaultError };
  }
};



export const registerZodI18n = (i18n: I18nInstance) => {
  i18nRef = i18n;
  z.setErrorMap(zodErrorMap);
  i18n.on("languageChanged", () => {
    z.setErrorMap(zodErrorMap);
  });
}
